import type { RoundSnapshot, RoundEvent, FeatureState } from "../contracts/RoundSnapshot";
import { FeatureFlowValidationError } from "./ValidationErrors";

type ActiveFeature = {
  featureIdentifier: string;
  enterEventIndex: number;
  initialRemaining?: bigint;
  cumulativeAwarded: bigint;
};

function parseOptionalNonNegativeBigInt(value: unknown, path: string): bigint | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new FeatureFlowValidationError(`${path}: Must be a non-empty string when provided.`);
  }
  if (!/^\d+$/.test(value)) {
    throw new FeatureFlowValidationError(`${path}: Must be a non-negative integer string, got "${value}".`);
  }
  return BigInt(value);
}

/** The free-spins naming rule stated on `RoundEvent.eventTypeName`: such an event must carry a feature state. */
function isFsNamedEvent(eventTypeName: string): boolean {
  const n = eventTypeName.toLowerCase();
  return n.startsWith("fs") || n.includes("fs_");
}

function getFeatureState(event: RoundEvent): FeatureState | undefined {
  return event.eventContent.featureState ?? undefined;
}

/**
 * Sum all `featureCountAwarded` values across `bonus_feature_won` actions in
 * the event's orderedActions. Missing or malformed fields throw.
 *
 * Slots announce retriggers via `bonus_feature_won`. Timing conventions
 * vary — some backends emit the announcement in the same event as the
 * `featureCountRemaining` bump, some emit it one event later (in the
 * cascade that settled the triggering scatters). We sum across events
 * cumulatively to tolerate both.
 */
function sumBonusFeatureWonAwardsInEvent(event: RoundEvent, eventPath: string): bigint {
  const actions = event.eventContent.orderedActions ?? [];
  let sum = 0n;
  for (let i = 0; i < actions.length; i++) {
    const action = actions[i];
    if (action.actionTypeName !== "bonus_feature_won") continue;
    const awardedRaw = (action.actionPayload as { featureCountAwarded?: unknown })
      ?.featureCountAwarded;
    if (typeof awardedRaw !== "string" || !/^\d+$/.test(awardedRaw)) {
      throw new FeatureFlowValidationError(
        `${eventPath}.eventContent.orderedActions[${i}](bonus_feature_won).actionPayload.featureCountAwarded: Must be a non-negative integer string, got ${JSON.stringify(awardedRaw)}.`
      );
    }
    sum += BigInt(awardedRaw);
  }
  return sum;
}

/**
 * Validates the feature loop (free spins) semantics:
 * - featureState.enter must start a feature
 * - featureState.update must happen only while active, with same featureIdentifier
 * - featureCountRemaining (if present) must not exceed the feature's award
 *   budget: initial enter count + cumulative bonus_feature_won.featureCountAwarded
 *   seen so far in the feature. Retriggers are supported via this rule —
 *   announcement timing (same event as the bump vs. one event later) is
 *   tolerated.
 * - accumulated feature win (if present) must be a valid non-negative integer string
 * - featureState.exit must close the feature
 * - fs_* events must not appear outside feature without featureState
 *
 * Server-authoritative: we don't compute; we only sanity-check consistency.
 */
export function validateFeatureFlowOrThrow(roundSnapshot: RoundSnapshot): void {
  let active: ActiveFeature | null = null;

  for (let i = 0; i < roundSnapshot.orderedEvents.length; i++) {
    const event = roundSnapshot.orderedEvents[i];
    const featureState = getFeatureState(event);

    const eventPath = `roundSnapshot.orderedEvents[${i}](${event.eventTypeName})`;

    if (!featureState && isFsNamedEvent(event.eventTypeName)) {
      throw new FeatureFlowValidationError(
        `${eventPath}: Event name looks like a feature-scoped event, but eventContent.featureState is missing.`
      );
    }

    if (!featureState) {
      continue;
    }

    const phase = featureState.featurePhase;
    const featureId = featureState.featureIdentifier;

    if (typeof featureId !== "string" || featureId.trim().length === 0) {
      throw new FeatureFlowValidationError(
        `${eventPath}.eventContent.featureState.featureIdentifier: Required.`,
      );
    }

    const remaining = parseOptionalNonNegativeBigInt(
      featureState.featureCountRemaining,
      `${eventPath}.eventContent.featureState.featureCountRemaining`,
    );

    parseOptionalNonNegativeBigInt(
      featureState.featureAccumulatedWinMinor,
      `${eventPath}.eventContent.featureState.featureAccumulatedWinMinor`,
    );

    if (phase === "enter") {
      if (active) {
        throw new FeatureFlowValidationError(
          `${eventPath}: featurePhase="enter" but feature "${active.featureIdentifier}" is already active (entered at index ${active.enterEventIndex}).`,
        );
      }

      active = {
        featureIdentifier: featureId,
        enterEventIndex: i,
        initialRemaining: remaining,
        // Awards that land on the enter event itself still count toward
        // this feature's budget (unusual but permissible).
        cumulativeAwarded: sumBonusFeatureWonAwardsInEvent(event, eventPath),
      };

      continue;
    }

    if (phase === "update") {
      if (!active) {
        throw new FeatureFlowValidationError(
          `${eventPath}: featurePhase="update" but no feature is active (missing prior featurePhase="enter").`,
        );
      }

      if (active.featureIdentifier !== featureId) {
        throw new FeatureFlowValidationError(
          `${eventPath}: featureIdentifier="${featureId}" does not match active feature "${active.featureIdentifier}".`,
        );
      }

      active.cumulativeAwarded += sumBonusFeatureWonAwardsInEvent(event, eventPath);

      if (remaining !== undefined && active.initialRemaining !== undefined) {
        const budget = active.initialRemaining + active.cumulativeAwarded;
        if (remaining > budget) {
          throw new FeatureFlowValidationError(
            `${eventPath}: featureCountRemaining=${remaining.toString()} exceeds feature budget ${budget.toString()} (initial enter count ${active.initialRemaining.toString()} + cumulative bonus_feature_won awards ${active.cumulativeAwarded.toString()} seen through this event).`,
          );
        }
      }

      continue;
    }

    if (phase === "exit") {
      if (!active) {
        throw new FeatureFlowValidationError(
          `${eventPath}: featurePhase="exit" but no feature is active (missing prior featurePhase="enter").`,
        );
      }

      if (active.featureIdentifier !== featureId) {
        throw new FeatureFlowValidationError(
          `${eventPath}: featureIdentifier="${featureId}" does not match active feature "${active.featureIdentifier}".`,
        );
      }

      active = null;
      continue;
    }

    throw new FeatureFlowValidationError(
      `${eventPath}.eventContent.featureState.featurePhase: Must be "enter" | "update" | "exit".`,
    );
  }

  if (active) {
    throw new FeatureFlowValidationError(
      `roundSnapshot: feature "${active.featureIdentifier}" entered at index ${active.enterEventIndex} but never exited.`,
    );
  }
}